import { useCallback, useMemo } from 'react'
import { useQueryClient } from 'react-query'

import {
  OrganizationSettingsResponseData,
  useUpdateMultipleOrganizationSettings,
  useUpdateOrganizationSettings,
  useUserOrganization,
  useUserOrganizationSettings,
} from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../enums/queryKeys'
import { useOrganizationSource } from '../../../hooks/useOrganizationSource'
import { QuickStartStatus } from '../enums/quickStartStatus'
import { QuickStartStep } from '../enums/quickStartStep'
import type { QuickStartStepConfig } from '../types/quickStartStepConfig'
import { getQuickStartSteps } from '../utils/getQuickStartSteps'
import { isQuickStartSkipped } from '../utils/isQuickStartSkipped'
import { useIsQuickStartEligible } from './useIsQuickStartEligible'
import { useTrackQuickStart } from './useTrackQuickStart'

const validSteps = new Set<string>(Object.values(QuickStartStep))

// Parse the raw comma-separated value
const parseCompletedSteps = (raw: string | undefined): QuickStartStep[] =>
  (raw ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter((value): value is QuickStartStep => validSteps.has(value))

export const useQuickStartProgress = () => {
  const queryClient = useQueryClient()
  const { organization, isLoading: isFetchingOrganization } = useUserOrganization()
  const {
    organizationSettings,
    isLoading: isFetchingOrganizationSettings,
    isPreviousData,
  } = useUserOrganizationSettings()
  const { update: updateOrganizationSettings } = useUpdateOrganizationSettings()
  const { update: updateMultipleSettings } = useUpdateMultipleOrganizationSettings()
  const { trackQuickStart } = useTrackQuickStart()

  const isLoading = isFetchingOrganization || isFetchingOrganizationSettings || isPreviousData

  const isSkipped = isQuickStartSkipped(organizationSettings?.quickStartStatus)
  const isEligible = useIsQuickStartEligible()
  const { isSourceBilly } = useOrganizationSource()

  const isEInvoicingApplicable = organization?.countryId === 'FR'
  // Billy orgs don't go through the Quick Start account-creation flow, so the step is meaningless for them.
  const isCreateAccountStepApplicable = !isSourceBilly

  const completedSteps = useMemo(
    () => parseCompletedSteps(organizationSettings?.quickStart),
    [organizationSettings?.quickStart],
  )

  const steps: QuickStartStepConfig[] = useMemo(
    () => getQuickStartSteps({ completedSteps, isCreateAccountStepApplicable, isEInvoicingApplicable }),
    [completedSteps, isCreateAccountStepApplicable, isEInvoicingApplicable],
  )

  const completionByStep = useMemo(
    () =>
      steps.reduce<Partial<Record<QuickStartStep, boolean>>>((stepCompletionMap, { step, isCompleted }) => {
        stepCompletionMap[step] = isCompleted
        return stepCompletionMap
      }, {}),
    [steps],
  )

  const incompleteStepsCount = useMemo(() => steps.filter((step) => !step.isCompleted).length, [steps])

  const isCompleted = incompleteStepsCount === 0

  const markStepDone = useCallback(
    (step: QuickStartStep) => {
      const isQuickStartDisabled =
        !organization?.id || step === QuickStartStep.CreateAccount || isLoading || isSkipped || !isEligible

      if (isQuickStartDisabled) {
        return
      }

      const latestOrganizationSettings = queryClient.getQueryData<OrganizationSettingsResponseData>([
        QueryKeys.UserOrganizationSettings,
        organization.id,
      ])

      if (!latestOrganizationSettings) {
        return
      }

      const latestCompletedSteps = parseCompletedSteps(latestOrganizationSettings.settings.quickStart)

      if (latestCompletedSteps.includes(step)) {
        return
      }

      const updatedCompletedSteps = [...latestCompletedSteps, step]
      const isLastStep = getQuickStartSteps({
        completedSteps: updatedCompletedSteps,
        isCreateAccountStepApplicable,
        isEInvoicingApplicable,
      }).every((stepConfig) => stepConfig.isCompleted)

      if (isLastStep) {
        updateMultipleSettings({
          organizationId: organization.id,
          payload: {
            quickStart: updatedCompletedSteps.join(','),
            quickStartStatus: QuickStartStatus.Completed,
          },
        })
        trackQuickStart('Quick Start Completed (Client)')

        return
      }

      updateOrganizationSettings({
        key: 'quickStart',
        organizationId: organization.id,
        value: updatedCompletedSteps.join(','),
      })
    },
    [
      organization?.id,
      isLoading,
      isSkipped,
      isEligible,
      isCreateAccountStepApplicable,
      isEInvoicingApplicable,
      queryClient,
      trackQuickStart,
      updateMultipleSettings,
      updateOrganizationSettings,
    ],
  )

  const skipQuickStart = useCallback(() => {
    if (!organization?.id || isLoading || isSkipped || !isEligible) {
      return
    }

    updateOrganizationSettings({
      key: 'quickStartStatus',
      organizationId: organization.id,
      value: QuickStartStatus.Skipped,
    })
  }, [organization?.id, isLoading, isSkipped, isEligible, updateOrganizationSettings])

  return {
    completionByStep,
    incompleteStepsCount,
    isCompleted,
    isEligible,
    isLoading,
    isSkipped,
    markStepDone,
    skipQuickStart,
    steps,
  }
}
